// /projects/:projectID/hotspots — Security Hotspots review workflow (T341).
//
// Hotspots were previously reachable only via the Issues page's "Hotspot"
// kind filter, which gave no access to the backend's dedicated review flow
// (GET /api/v1/hotspots, POST /hotspots/{id}/transition). This page is the
// hotspot-specific analog of Issues.tsx: a status-filtered, infinite-scroll
// list that opens a review drawer (HotspotDetailDrawer) instead of the
// general issue drawer.
//
// Surface of this page:
//   - Header: project breadcrumb, page title + shown count.
//   - Filter bar: review-status chips (multi-toggle, mirrors Issues.tsx's
//     severity/kind chip pattern).
//   - Result list: status chip + message + file:line + detected_at.
//   - Infinite scroll: same IntersectionObserver-sentinel pattern as
//     Issues.tsx, keyed off the backend's next_cursor.
//   - Detail drawer: HotspotDetailDrawer, opened via `?hotspot=<id>` so deep
//     links and back/forward work the same way Issues.tsx's `?issue=<id>` does.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  ArrowLeft,
  Eye,
  FileWarning,
  Filter,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import {
  ApiError,
  HOTSPOT_REVIEW_STATUSES,
  HotspotListParams,
  HotspotListResponse,
  HotspotReviewStatus,
  Issue,
  Project,
  buildHotspotsURL,
  fetcher,
} from '../lib/api';
import { HotspotDetailDrawer } from '../components/HotspotDetailDrawer';
import { PageHeader } from '../components/ui';

const PAGE_SIZE = 50; // matches db.DefaultHotspotListLimit

const STATUS_TONE: Record<HotspotReviewStatus, string> = {
  to_review: 'bg-warning/10 text-warning border-warning/40',
  acknowledged: 'bg-accent/10 text-accent border-accent/30',
  fixed: 'bg-accent-muted text-accent border-accent/30',
  safe: 'bg-bg-inset text-fg-muted border-border',
};

const STATUS_LABEL: Record<HotspotReviewStatus, string> = {
  to_review: 'To review',
  acknowledged: 'Acknowledged',
  fixed: 'Fixed',
  safe: 'Safe',
};

function parseStatus(raw: string | null): HotspotReviewStatus | undefined {
  return HOTSPOT_REVIEW_STATUSES.find((s) => s === raw);
}

export function Hotspots() {
  const { projectID } = useParams<{ projectID: string }>();
  const id = projectID ?? '';

  const [search, setSearch] = useSearchParams();
  const statusFilter = parseStatus(search.get('status'));

  const setStatus = (next: HotspotReviewStatus | undefined) => {
    const params = new URLSearchParams(search);
    if (next) params.set('status', next);
    else params.delete('status');
    setSearch(params, { replace: true });
  };

  const selectedID = search.get('hotspot') ?? '';
  const openHotspot = (hotspotId: string) => {
    const params = new URLSearchParams(search);
    if (hotspotId) params.set('hotspot', hotspotId);
    else params.delete('hotspot');
    setSearch(params);
  };
  const closeHotspot = () => {
    if (!search.has('hotspot')) return;
    const params = new URLSearchParams(search);
    params.delete('hotspot');
    setSearch(params, { replace: true });
  };

  const projectKey = id ? `/api/v1/projects/${id}` : null;
  const project = useSWR<Project>(projectKey, fetcher);

  const baseParams = useMemo<Omit<HotspotListParams, 'cursor'>>(
    () => ({
      project_id: id,
      status: statusFilter,
      limit: PAGE_SIZE,
    }),
    [id, statusFilter],
  );

  const getKey = useCallback(
    (index: number, prev: HotspotListResponse | null): string | null => {
      if (!id) return null;
      if (index === 0) return buildHotspotsURL(baseParams);
      if (!prev || !prev.next_cursor) return null;
      return buildHotspotsURL({ ...baseParams, cursor: prev.next_cursor });
    },
    [id, baseParams],
  );

  const {
    data: pages,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  } = useSWRInfinite<HotspotListResponse>(getKey, fetcher, {
    revalidateFirstPage: false,
  });

  const hotspots: Issue[] = useMemo(
    () => (pages ?? []).flatMap((p) => p.items ?? []),
    [pages],
  );
  const lastPage = pages?.[pages.length - 1];
  const hasMore = Boolean(lastPage?.next_cursor);
  const isLoadingMore = isValidating && pages !== undefined && size > pages.length;

  const selectedHotspot = useMemo<Issue | null>(() => {
    if (!selectedID) return null;
    return hotspots.find((h) => h.id === selectedID) ?? null;
  }, [selectedID, hotspots]);

  // Re-fetch all loaded pages after a successful transition so the list
  // reflects the new status (and drops out of the current filter if it no
  // longer matches, e.g. transitioning out of "to_review" while filtered on it).
  const handleTransitioned = () => {
    void mutate();
  };

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasMore) return;
    if (isLoadingMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSize((s) => s + 1);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, isLoadingMore, setSize]);

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <BackToProject projectID={id} projectName={project.data?.name} />
      <PageHeader
        eyebrow="orbital · hotspots"
        title="Security Hotspots"
        meta={
          hotspots.length > 0 ? (
            <>
              {hotspots.length}
              {hasMore ? '+' : ''} shown
            </>
          ) : null
        }
        description={
          <>
            Code that needs a human decision on whether it&rsquo;s exploitable.
            Review each finding and mark it safe, fixed, or acknowledged.
          </>
        }
      />

      <StatusFilterBar status={statusFilter} onChange={setStatus} />

      <div className="mt-6">
        {error ? <RequestError error={error} /> : null}
        {!error && isLoading ? <Loading label="Loading hotspots…" /> : null}
        {!error && !isLoading && hotspots.length === 0 ? (
          <EmptyState hasFilter={statusFilter !== undefined} />
        ) : null}
        {!error && hotspots.length > 0 ? (
          <HotspotList hotspots={hotspots} selectedID={selectedID} onSelect={openHotspot} />
        ) : null}

        <div
          ref={sentinelRef}
          className="mt-4 py-4 flex items-center justify-center gap-2 text-xs text-fg-subtle"
        >
          {(() => {
            if (hasMore && isLoadingMore) return (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                <span>Loading more…</span>
              </>
            );
            if (hasMore) return <span>Scroll for more</span>;
            if (hotspots.length > 0) return <span>End of results.</span>;
            return null;
          })()}
        </div>
      </div>

      <HotspotDetailDrawer
        hotspot={selectedHotspot}
        requestedID={selectedID || undefined}
        onClose={closeHotspot}
        onTransitioned={handleTransitioned}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

function BackToProject({
  projectID,
  projectName,
}: {
  readonly projectID: string;
  readonly projectName?: string;
}) {
  return (
    <Link
      to={`/projects/${projectID}`}
      className="
        inline-flex items-center gap-1.5 mb-5 text-xs font-semibold
        uppercase tracking-[0.08em] text-fg-muted hover:text-fg
        transition-colors
      "
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{projectName ? `Back to ${projectName}` : 'Back to project'}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function StatusFilterBar({
  status,
  onChange,
}: {
  readonly status: HotspotReviewStatus | undefined;
  readonly onChange: (next: HotspotReviewStatus | undefined) => void;
}) {
  return (
    <section
      aria-label="Hotspot filters"
      className="orb-card p-4 flex flex-wrap items-center gap-x-6 gap-y-3"
    >
      <div className="inline-flex items-center gap-1.5 text-fg-muted">
        <Filter className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="orb-section-title">Review status</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {HOTSPOT_REVIEW_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={status === s}
            onClick={() => onChange(status === s ? undefined : s)}
            className={[
              'orb-badge border cursor-pointer transition-colors',
              status === s
                ? STATUS_TONE[s]
                : 'bg-bg-inset text-fg-muted border-border hover:text-fg hover:bg-bg-hover',
            ].join(' ')}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function HotspotList({
  hotspots,
  selectedID,
  onSelect,
}: {
  readonly hotspots: Issue[];
  readonly selectedID: string;
  readonly onSelect: (id: string) => void;
}) {
  return (
    <ul className="orb-card divide-y divide-border" aria-label="Security hotspots">
      {hotspots.map((h) => (
        <HotspotRow key={h.id} hotspot={h} selected={h.id === selectedID} onSelect={onSelect} />
      ))}
    </ul>
  );
}

function HotspotRow({
  hotspot,
  selected,
  onSelect,
}: {
  readonly hotspot: Issue;
  readonly selected: boolean;
  readonly onSelect: (id: string) => void;
}) {
  const status = HOTSPOT_REVIEW_STATUSES.find((s) => s === hotspot.hotspot_review_status) ?? 'to_review';
  const fileLine = hotspot.file_path
    ? `${hotspot.file_path}:${hotspot.line_start || 1}`
    : '—';
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(hotspot.id)}
        aria-pressed={selected}
        aria-label={`Open hotspot: ${hotspot.message || hotspot.rule_id || hotspot.id}`}
        className={[
          'w-full text-left px-4 py-3 text-sm',
          'transition-colors hover:bg-bg-hover focus:outline-none focus:bg-bg-hover',
          'focus-visible:ring-2 focus-visible:ring-accent/40',
          selected ? 'bg-bg-hover' : '',
        ].join(' ')}
      >
        <div className="flex items-start gap-3">
          <span className={`orb-badge border shrink-0 ${STATUS_TONE[status]}`}>
            <Eye className="h-3 w-3" aria-hidden="true" />
            <span>{STATUS_LABEL[status]}</span>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-fg font-medium leading-snug">
              {hotspot.message || <span className="text-fg-subtle">(no message)</span>}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
              <span className="orb-code text-xs truncate max-w-[28rem]">{fileLine}</span>
              {hotspot.rule_id ? <span className="text-fg-muted">{hotspot.rule_id}</span> : null}
              {hotspot.detected_at ? <span>{formatTimestamp(hotspot.detected_at)}</span> : null}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Shared bits (loading / error / empty)
// ---------------------------------------------------------------------------

function Loading({ label }: { readonly label: string }) {
  return (
    <div className="orb-card p-6 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function RequestError({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  return (
    <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-1">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
    </div>
  );
}

function EmptyState({ hasFilter }: { readonly hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="orb-card p-8 text-center">
        <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
          <FileWarning className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="font-display text-lg font-semibold text-fg mb-2">
          No hotspots match this filter.
        </h2>
        <p className="text-fg-muted text-sm max-w-md mx-auto">
          Clear the review-status filter to see hotspots in every state.
        </p>
      </div>
    );
  }
  return (
    <div className="orb-card p-8 text-center">
      <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
      </div>
      <h2 className="font-display text-lg font-semibold text-fg mb-2">
        No security hotspots.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        Hotspots are flagged by rules marked <span className="orb-code">is_hotspot</span> — code
        that needs a human decision, not necessarily a fix. None have been found on this project
        yet.
      </p>
    </div>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
